import {AdditionalFeesMap, Car, Voyage, RelatedCar} from '../types';
import {BillOfLadingData} from '../prints/BillOfLading';
import {formatDateOrToday} from './tableUtils';

// ============================================================================
// Make/Model Extraction Utilities
// ============================================================================

/**
 * Extract make/model string from a car or related car with consistent fallback to manual_make_model
 */
export function extractMakeModel(entity: {
    make?: { name?: string } | null;
    model?: { name?: string } | null;
    manual_make_model?: string | null
}): string {
    const make = entity.make?.name || '';
    const model = entity.model?.name || '';
    if (make && model) {
        return `${make} ${model}`;
    }
    return (entity.manual_make_model || `${make}${model}`).trim();
}

// ============================================================================
// Related Cars Utilities
// ============================================================================

// Insurance is a regular additional fee that only main units carry (added via the
// Insurance bulk actions). Matched case-insensitively, same as the backend.
const INSURANCE_FEE_NAME = 'insurance';

/**
 * Drop the insurance fee - used when an existing unit is picked as a related unit.
 */
export function withoutInsuranceFee(fees: AdditionalFeesMap | undefined): AdditionalFeesMap {
    return Object.fromEntries(
        Object.entries(fees || {}).filter(([name]) => name.trim().toLowerCase() !== INSURANCE_FEE_NAME)
    );
}

/**
 * Sort related cars: Attached units first, then STC (Said To Contain)
 */
export function sortRelatedCarsByType(relatedCars: RelatedCar[]): RelatedCar[] {
    return [...relatedCars].sort((a, b) => {
        if (a.relationship_type === 'Attached' && b.relationship_type !== 'Attached') return -1;
        if (a.relationship_type !== 'Attached' && b.relationship_type === 'Attached') return 1;
        return 0;
    });
}

// ============================================================================
// Measurement Utilities
// ============================================================================

/**
 * Calculate CBM (Cubic Meters) for a car
 * Uses car's cbm if set, otherwise calculates from dimensions, falls back to model's cbm
 */
export function calculateMeasurement(car: Car): string {
    const cbm = car.cbm ||
        (car.length && car.width && car.height ? car.length * car.width * car.height : null) ||
        car.model?.cbm ||
        0;
    return `${Number(cbm).toFixed(3)} CBM`;
}

/**
 * Calculate gross weight for a car
 * Uses car's weight first, falls back to model's weight
 */
export function calculateGrossWeight(car: Car): string {
    const weight = car.weight || car.model?.weight || 0;
    return `${weight.toLocaleString()} kg`;
}

// ============================================================================
// Bill of Lading Transformation
// ============================================================================

export interface BillOfLadingOptions {
    isOriginal: boolean;
    includeRelated: boolean;
}

/**
 * Build BL description string with main car and optionally related cars
 */
export function buildBLDescription(car: Car, includeRelated: boolean): string {
    const mainMakeModel = extractMakeModel(car);
    const descriptions = [mainMakeModel || '-'];

    if (includeRelated && car.related_cars?.length) {
        const sortedRelated = sortRelatedCarsByType(car.related_cars);
        sortedRelated.forEach(rc => {
            const rcMakeModel = extractMakeModel(rc);
            const relationshipType = rc.relationship_type || 'STC';
            descriptions.push(`↳ ${relationshipType} ${rcMakeModel || '-'} CH: ${rc.chassis_number || '-'}`);
        });
    }

    return descriptions.join('\n');
}

/**
 * Convert a Car to BillOfLadingData
 * This is the single source of truth for BL transformation
 *
 * @param car - The car to convert (must have voyage loaded)
 * @param options - Print options (isOriginal, includeRelated)
 * @param voyageOverride - Optional voyage data to use instead of car.voyage (for BillOfLadingPage)
 * @returns BillOfLadingData or null if car has no voyage
 */
export function convertCarToBillOfLadingData(
    car: Car,
    options: BillOfLadingOptions,
    voyageOverride?: Voyage | null
): BillOfLadingData | null {
    const voyage = voyageOverride || car.voyage;
    if (!voyage) {
        return null;
    }

    return {
        blNumber: car.bl_number || `CLP${car.reference_number}`,
        refNumber: car.booking_reference || '',
        voyageNumber: voyage.voyage_code,
        shipper: {
            name: car.shipper?.name || 'N/A',
            address: car.shipper?.address || '',
        },
        consignee: {
            name: car.consignee?.name || car.name || 'N/A',
            address: car.consignee?.address || car.address || '',
        },
        notifyAddress: '',
        preCarriagePlace: '',
        vesselName: voyage.vessel_description,
        portOfLoading: voyage.port_name || '',
        portOfDischarge: car.port?.port || car.port?.code || '',
        deliveryPlace: '',
        marksAndNumbers: car.chassis_number,
        numberOfUnits: '1',
        description: buildBLDescription(car, options.includeRelated),
        grossWeight: calculateGrossWeight(car),
        measurement: calculateMeasurement(car),
        freightPayableAt: voyage.port_name || '',
        placeOfIssue: voyage.port_name || '',
        dateOfIssue: formatDateOrToday(voyage.departure),
        numberOfOriginalBLs: '3',
        isOriginal: options.isOriginal,
        loaded: car.loaded || false,
    };
}

/**
 * Whether a set of cars may be combined onto a single Bill of Lading.
 * Rule: same shipper, consignee, POD, and booking. (Same voyage/POL is
 * already guaranteed because the BL page loads cars from one voyage.)
 */
export function carsCanCombine(cars: Car[]): boolean {
    if (cars.length < 2) return false;
    const key = (c: Car) => [
        c.shipper_id || c.shipper?.id || '',
        c.consignee_id || c.consignee?.id || c.consignee_name || c.name || '',
        c.port_id || c.port?.id || '',
        c.voyage_id || c.voyage?.id || '',
        c.booking_reference || '',
    ].join('|');
    const first = key(cars[0]);
    return cars.every(c => key(c) === first);
}

const rawCbm = (c: Car): number =>
    c.cbm ||
    (c.length && c.width && c.height ? c.length * c.width * c.height : 0) ||
    c.model?.cbm ||
    0;

/**
 * Merge several cars onto ONE Bill of Lading. The shared BL number is
 * CLP + the lowest reference number ("oldest"). All units' chassis go into
 * marks, descriptions concatenate, and weight/measurement/unit count sum.
 * Shipper/consignee come from the oldest car (combine assumes they match).
 */
export function mergeCarsToBillOfLadingData(
    cars: Car[],
    options: BillOfLadingOptions,
    voyageOverride?: Voyage | null
): BillOfLadingData | null {
    if (cars.length === 0) return null;
    const sorted = [...cars].sort((a, b) => Number(a.reference_number) - Number(b.reference_number));
    const base = convertCarToBillOfLadingData(sorted[0], options, voyageOverride);
    if (!base || sorted.length === 1) return base;

    const totalWeight = sorted.reduce((s, c) => s + (c.weight || c.model?.weight || 0), 0);
    const totalCbm = sorted.reduce((s, c) => s + rawCbm(c), 0);
    const unitCount = sorted.reduce(
        (n, c) => n + 1 + (options.includeRelated ? (c.related_cars?.length || 0) : 0),
        0
    );

    return {
        ...base,
        blNumber: `CLP${sorted[0].reference_number}`,
        marksAndNumbers: sorted.map(c => c.chassis_number).join('\n'),
        numberOfUnits: String(unitCount),
        description: sorted.map(c => buildBLDescription(c, options.includeRelated)).join('\n'),
        grossWeight: `${totalWeight.toLocaleString()} kg`,
        measurement: `${totalCbm.toFixed(3)} CBM`,
    };
}

/**
 * Build the Bills of Lading to print for a set of target cars, auto-expanding
 * any car that belongs to a persisted combined group (shared bl_number) into a
 * single combined BL. `pool` is the larger set used to find a group's other
 * members (e.g. all cars in the voyage). Each combined group prints once even
 * if several of its members are in `targetCars`.
 */
export function buildBillsForPrint(
    targetCars: Car[],
    pool: Car[],
    options: BillOfLadingOptions,
    voyageOverride?: Voyage | null
): BillOfLadingData[] {
    const bills: BillOfLadingData[] = [];
    const doneGroups = new Set<string>();

    for (const car of targetCars) {
        if (car.bl_number) {
            if (doneGroups.has(car.bl_number)) continue;
            doneGroups.add(car.bl_number);
            const members = pool.filter(c => c.bl_number === car.bl_number);
            // Fall back to the car itself if the pool doesn't contain the group.
            const merged = mergeCarsToBillOfLadingData(members.length ? members : [car], options, voyageOverride);
            if (merged) bills.push(merged);
        } else {
            const single = convertCarToBillOfLadingData(car, options, voyageOverride);
            if (single) bills.push(single);
        }
    }

    return bills;
}

// ============================================================================
// Voyage Code Utilities
// ============================================================================

export const createVoyageCodeMap = (voyages: Voyage[]): Map<string, string> => {
    const map = new Map<string, string>();
    voyages.forEach(voyage => {
        map.set(voyage.id, voyage.voyage_code);
    });
    return map;
};

export const attachVoyageCodes = (
    cars: Car[],
    codeLookup: Map<string, string> = new Map()
): Car[] => {
    return cars.map(car => {
        const existingCode = car.voyage?.voyage_code || car.voyage_code;
        const lookupCode = car.voyage_id ? codeLookup.get(car.voyage_id) : undefined;
        const voyage_code = existingCode || lookupCode;

        if (voyage_code && car.voyage_code !== voyage_code) {
            return {...car, voyage_code};
        }

        return car;
    });
};
